<div class="shell" [attr.data-collapsed]="collapsed">

  <!-- Mobile: dark backdrop behind the slide-in sidebar drawer -->
  <div class="sb-backdrop" *ngIf="mobileSidebarOpen" (click)="mobileSidebarOpen = false"></div>

  <!-- ── Sidebar ─────────────────────────────────────────────── -->
  <aside class="sidebar" [class.sb-mobile-open]="mobileSidebarOpen">
    <div class="sb-head">
      <div class="sb-brand">
        <img class="brand-logo" src="assets/img/shaji-brand.png" alt="Shaji AI" />
        <span class="brand-name">Shaji AI</span>
      </div>
      <button class="icon-btn" (click)="collapsed = !collapsed" [attr.aria-label]="collapsed ? 'Expand sidebar' : 'Collapse sidebar'" title="Toggle sidebar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="16" rx="2.5"/><path d="M9 4v16"/>
        </svg>
      </button>
    </div>

    <button class="sb-newchat" (click)="newChat()">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 5v14M5 12h14"/>
      </svg>
      <span>New chat</span>
      <span class="kbd">⌘N</span>
    </button>

    <div class="sb-search">
      <svg class="sb-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
      </svg>
      <input type="text" placeholder="Search conversations" [(ngModel)]="searchQuery" (ngModelChange)="onSearch()" />
    </div>

    <div class="sb-list"
         (touchstart)="onSidebarTouchStart($event)"
         (touchmove)="onSidebarTouchMove($event)"
         (touchend)="onSidebarTouchEnd()">
      <!-- v2.9 (C.4): pull-to-refresh indicator. Translates down as the user pulls. -->
      <div class="ptr-indicator" [style.transform]="'translateY(' + ptrPullDist + 'px)'" [class.ptr-armed]="ptrPullDist >= 60" [class.ptr-refreshing]="ptrRefreshing">
        <span *ngIf="ptrRefreshing" class="ptr-spinner"></span>
        <span *ngIf="!ptrRefreshing && ptrPullDist > 0">{{ ptrPullDist >= 60 ? 'Release to refresh' : 'Pull to refresh' }}</span>
      </div>
      <!-- F.2: saved / favorite queries — tap to re-run, × to remove. Hidden while searching. -->
      <ng-container *ngIf="favorites.length > 0 && !searchQuery">
        <div class="sb-section">⭐ Saved questions</div>
        <div class="sb-favs">
          <span class="sb-fav" *ngFor="let f of favorites" (click)="runFavorite(f)" [title]="f.query_text">
            <span class="sb-fav-label">{{ f.label }}</span>
            <span class="sb-fav-del" (click)="removeFavorite(f, $event)" title="Remove">×</span>
          </span>
        </div>
      </ng-container>

      <ng-container *ngFor="let group of filteredGroups">
        <div class="sb-section">{{ group.label }}</div>
        <button *ngFor="let s of group.sessions"
                class="sb-item"
                [attr.data-active]="s.session_id === currentSessionId"
                (click)="openSession(s)"
                [title]="s.title">
          <span class="sb-item-title">{{ s.title }}</span>
          <span class="sb-item-meta">{{ state.relativeTime(s.updatedAt) }}</span>
          <span class="sb-item-act" (click)="renameChat(s, $event)" title="Rename">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
            </svg>
          </span>
          <span class="sb-item-del" (click)="deleteSession(s, $event)" title="Delete">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 6l12 12M18 6 6 18"/>
            </svg>
          </span>
        </button>
      </ng-container>

      <!-- F.1: content-search hits (matched inside messages, not titles). -->
      <ng-container *ngIf="contentMatches.length > 0">
        <div class="sb-section">Found in messages</div>
        <button *ngFor="let m of contentMatches" class="sb-item sb-item-match" (click)="openContentMatch(m)" [title]="m.title">
          <span class="sb-item-title">{{ m.title }}</span>
          <span class="sb-match-snippet">{{ m.snippet }}</span>
        </button>
      </ng-container>

      <div *ngIf="filteredGroups.length === 0 && contentMatches.length === 0" style="padding: 24px 14px; text-align: center; color: var(--text-faint); font-size: 12px;">
        {{ searchQuery ? 'No conversations match' : 'No previous chats yet' }}
      </div>
    </div>

    <div class="sb-foot">
      <div class="sb-avatar">{{ initials }}</div>
      <div class="sb-foot-text">
        <div class="sb-foot-name">{{ employeeName }}</div>
        <div class="sb-foot-plan">{{ userType }} · Workspace</div>
      </div>
      <button class="icon-btn" (click)="openReportPanel()" [disabled]="isReporting" title="Report an issue">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 22V4a2 2 0 0 1 2-2h11l1 4h3v8h-7l-1-4H6"/>
        </svg>
      </button>
    </div>
  </aside>

  <!-- ── Main pane ────────────────────────────────────────────── -->
  <main class="main">

    <!-- Report status toast (auto-dismisses) -->
    <div class="report-toast" *ngIf="reportToast" [attr.data-type]="reportToast.type">{{ reportToast.text }}</div>

    <!-- Report-an-issue popup: ask the user to describe the problem before sending. -->
    <div class="report-modal-backdrop" *ngIf="reportModalOpen" (click)="cancelReport()">
      <div class="report-modal" (click)="$event.stopPropagation()">
        <div class="report-modal-head">
          <span>Report an issue</span>
          <button class="report-modal-x" (click)="cancelReport()" title="Close">×</button>
        </div>
        <p class="report-modal-sub">Tell us what went wrong, in as much detail as you can — what you asked, what you expected, and what actually happened. Your current screen and this chat are attached automatically.</p>
        <textarea class="report-modal-text"
                  [(ngModel)]="reportText"
                  rows="6"
                  maxlength="4000"
                  placeholder="e.g. I asked for last month's sales for Allied Trading and the total looked too low — it should be around AED 200K…"
                  autofocus></textarea>
        <div class="report-modal-actions">
          <button class="report-btn-cancel" (click)="cancelReport()">Cancel</button>
          <button class="report-btn-send" [disabled]="!reportText.trim()" (click)="submitReport()">Send report</button>
        </div>
      </div>
    </div>

    <div class="topbar">
      <!-- Mobile-only hamburger: opens the chats drawer (ChatGPT-style) -->
      <button class="icon-btn hamburger" (click)="mobileSidebarOpen = true" aria-label="Open chats" title="Chats">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
          <path d="M4 6h16M4 12h16M4 18h16"/>
        </svg>
      </button>
      <div class="topbar-title">
        <span class="dot"></span>
        <span class="topbar-title-text">{{ currentTitle }}</span>
      </div>
      <div class="topbar-actions">
        <span class="model-pill">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6"/>
          </svg>
          <span>Shaji Sonnet 4.6</span>
          <span class="badge">AI</span>
        </span>
        <button class="icon-btn" (click)="exportChat()" *ngIf="messages.length > 1" title="Export">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 3v12M5 10l7 7 7-7M5 21h14"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Empty state -->
    <ng-container *ngIf="showEmpty; else threadView">
      <div class="empty">
        <div class="empty-orb">
          <div class="empty-orb-ring"></div>
          <div class="empty-orb-ring"></div>
          <div class="empty-orb-core">
            <img src="assets/img/shaji-brand.png" alt="Shaji" />
          </div>
        </div>
        <h1 class="empty-greeting">{{ greeting }}</h1>
        <p class="empty-sub">What would you like to work on?</p>
        <div class="suggest-grid">
          <button *ngFor="let s of emptySuggestions" class="suggest" (click)="selectSuggestion(s.ask)">
            <div class="suggest-top">
              <svg *ngIf="s.icon === 'chart'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 14l4-4 4 4 5-6"/></svg>
              <svg *ngIf="s.icon === 'users'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              <svg *ngIf="s.icon === 'inventory'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.27 6.96 12 12.01l8.73-5.05M12 22.08V12"/></svg>
              <svg *ngIf="s.icon === 'bulb'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6M10 22h4M12 2a6 6 0 0 0-4 10.5c1 .9 2 2 2 3.5h4c0-1.5 1-2.6 2-3.5A6 6 0 0 0 12 2z"/></svg>
              <span class="suggest-title">{{ s.title }}</span>
            </div>
            <span class="suggest-sub">{{ s.sub }}</span>
          </button>
        </div>
      </div>
    </ng-container>

    <!-- Thread -->
    <ng-template #threadView>
      <div class="thread-wrap" #threadEl (scroll)="onThreadScroll()">
        <div class="thread">
          <ng-container *ngFor="let msg of messages; let i = index; trackBy: trackByIndex">

            <!-- AI message -->
            <div class="msg" data-role="ai" *ngIf="msg.role === 'ai'" [style.--msg-i]="i">
              <div class="avatar ai" [class.thinking]="!msg.finalized">
                <img src="assets/img/shaji-brand.png" alt="Shaji" />
              </div>
              <div class="msg-content">
                <div class="bubble">

                  <!-- Live progress — box-less; the single animated icon is the avatar
                       on the left (blinks while working). Text cycles brand/paint phrases. -->
                  <div class="sw-think" *ngIf="!msg.finalized && !msg.text" aria-live="polite">
                    <span class="sw-think-text">{{ (state.thinkingDisplay$ | async) }}<i class="sw-think-caret"></i></span>
                    <span class="sw-think-time">{{ elapsedSecs }}s</span>
                  </div>

                  <!-- Finished — subtle one-liner (no box), tap to see the steps it took -->
                  <div class="sw-done" *ngIf="msg.finalized && msg.steps && msg.steps.length > 0">
                    <button class="sw-done-toggle" (click)="toggleSteps(i)" [attr.aria-expanded]="msg.stepsOpen">
                      <span class="sw-done-check">✓</span>
                      <span>Done in {{ state.formatDuration(msg.processingDuration) }}</span>
                      <span class="sw-done-count">· {{ msg.steps.length }} step{{ msg.steps.length !== 1 ? 's' : '' }}</span>
                      <span class="sw-done-chev">{{ msg.stepsOpen ? '▾' : '▸' }}</span>
                    </button>
                    <div class="sw-done-steps" *ngIf="msg.stepsOpen">
                      <div class="sw-step" *ngFor="let step of msg.steps">
                        <span class="sw-step-dot done">✓</span>
                        <span>{{ step }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Chart -->
                  <div class="ai-chart" *ngIf="msg.chartData && msg.chartOptions">
                    <div class="ai-chart-title" *ngIf="msg.chartData.title">{{ msg.chartData.title }}</div>
                    <!-- Chart-type switcher toolbar (above chart) -->
                    <div class="chart-types">
                      <button *ngFor="let t of availableChartTypes(msg.chartData?.type)"
                              class="chart-type-btn"
                              [class.active]="msg.chartData?.type === t.id"
                              (click)="switchChartType(i, t.id)"
                              [title]="t.label"
                              [attr.aria-label]="t.label">
                        <span class="chart-type-icon">{{ t.icon }}</span>
                      </button>
                    </div>
                    <div echarts
                         [options]="msg.chartOptions"
                         [autoResize]="true"
                         style="width: 100%; height: 320px;">
                    </div>
                    <div class="ai-chart-hint">Click any data point for details</div>
                  </div>

                  <!-- Visit heat-map (getVisitMap) -->
                  <div class="ai-map" *ngIf="msg.mapData">
                    <div class="ai-map-title">🗺️ {{ msg.mapData.title || 'Visit map' }}</div>
                    <div class="ai-map-canvas" [visitMap]="msg.mapData"></div>
                    <div class="ai-map-legend">{{ msg.mapData.caption || ('Hotter zones = more visits · tap a pin for the customer · ' + (msg.mapData.totals?.customers || 0) + ' customers · ' + (msg.mapData.totals?.visits || 0) + ' visits') }} · ⛶ full screen · ⤓ download (top-left)</div>
                  </div>

                  <!-- Text -->
                  <span *ngIf="msg.text" class="ai-msg-text"
                        [innerHTML]="state.formatMessage(msg.text, !msg.finalized)"
                        (click)="onMessageClick($event, msg)"></span>
                  <span *ngIf="msg.typing && msg.text" class="caret"></span>

                  <!-- Generated document downloads (createDocument) -->
                  <div class="sw-files" *ngIf="msg.files?.length">
                    <a class="sw-file-card" *ngFor="let f of msg.files"
                       [href]="state.resolveFileUrl(f.url)" target="_blank" rel="noopener" download>
                      <span class="sw-file-icon" [attr.data-fmt]="f.format">{{ state.fileEmoji(f.format) }}</span>
                      <span class="sw-file-meta">
                        <span class="sw-file-name">{{ f.name }}</span>
                        <span class="sw-file-sub">{{ state.fileLabel(f.format) }} · {{ f.sizeKb }} KB · Tap to download</span>
                      </span>
                      <svg class="sw-file-dl" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </a>
                  </div>

                  <!-- Read aloud (TTS) -->
                  <button type="button"
                          *ngIf="speechSupported && msg.finalized && msg.text"
                          class="speak-btn"
                          [class.speaking]="currentSpeakingIndex === i && isSpeaking"
                          [title]="currentSpeakingIndex === i && isSpeaking ? 'Stop' : 'Read aloud'"
                          (click)="toggleSpeak(msg.text, i)">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                    </svg>
                  </button>

                  <!-- Copy answer -->
                  <button type="button"
                          *ngIf="msg.finalized && msg.text"
                          class="speak-btn"
                          [class.speaking]="copiedIdx === i"
                          [title]="copiedIdx === i ? 'Copied!' : 'Copy answer'"
                          (click)="copyMsg(i, msg.text)">
                    <svg *ngIf="copiedIdx !== i" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    <svg *ngIf="copiedIdx === i" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </button>

                  <!-- Regenerate — re-ask the last question for a fresh answer (only on the latest reply) -->
                  <button type="button"
                          *ngIf="msg.finalized && msg.text && i === messages.length - 1 && !isStreaming && !isLoading"
                          class="speak-btn"
                          title="Regenerate this answer"
                          (click)="regenerate()">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M23 4v6h-6M1 20v-6h6"/>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- User message -->
            <div class="msg" data-role="user" *ngIf="msg.role === 'user'" [style.--msg-i]="i">
              <div class="msg-content" style="display: flex; flex-direction: column; align-items: flex-end; max-width: 100%;">
                <div class="bubble" [attr.data-delivery]="msg.delivery || null">
                  <span *ngIf="msg.screenAttached" class="screen-chip" title="Your current screen was attached to this message">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                    Screen attached
                  </span>
                  {{ msg.text }}
                </div>
                <!-- v2.9 (C.2): delivery indicator under the bubble -->
                <div class="delivery-row" *ngIf="msg.delivery && msg.delivery !== 'delivered'">
                  <span *ngIf="msg.delivery === 'pending'" class="delivery-pending" title="Delivering…">
                    <span class="delivery-dot"></span><span class="delivery-dot"></span><span class="delivery-dot"></span>
                  </span>
                  <ng-container *ngIf="msg.delivery === 'failed'">
                    <span class="delivery-failed">⚠ {{ msg.deliveryError || 'Failed to send' }}</span>
                    <button type="button" class="delivery-retry" (click)="retryFailed(i)" title="Retry sending">↻ Retry</button>
                  </ng-container>
                </div>
              </div>
              <div class="avatar user">{{ initials }}</div>
            </div>

          </ng-container>

          <!-- Jump-to-latest pill: appears when you've scrolled up while a reply streams -->
          <button type="button" class="cp-jump" *ngIf="showJumpBtn" (click)="jumpToLatest()" aria-label="Jump to latest">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
            <span>Latest</span>
          </button>
        </div>
      </div>
    </ng-template>

    <!-- Composer (chips sit in normal flow above composer to avoid overlap) -->
    <div class="composer-wrap">

      <!-- Voice listening popup — floats above the composer while mic is hot. -->
      <div class="voice-popup" *ngIf="isListening" [class.product]="productVoiceActive" role="status" aria-live="polite">
        <button type="button" class="voice-popup-close" (click)="closeVoicePopup()" aria-label="Close voice input">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div class="voice-popup-header">
          <ng-container *ngIf="!productVoiceActive">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg>
            <span>Listening…</span>
          </ng-container>
          <ng-container *ngIf="productVoiceActive">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <span>Say a product name…</span>
          </ng-container>
          <span class="voice-popup-rec"><span class="rec-dot"></span>REC</span>
        </div>
        <div class="voice-wave" aria-hidden="true">
          <span></span><span></span><span></span><span></span><span></span>
          <span></span><span></span><span></span><span></span>
        </div>
        <div class="voice-transcript" [class.has-text]="!!liveTranscript">{{ liveTranscript }}</div>
        <div class="voice-lang-row">
          <button type="button" class="voice-lang-btn" [class.open]="langPickerOpen"
                  [disabled]="isTranscribing"
                  (click)="langPickerOpen = !langPickerOpen"
                  title="Speech recognition language">
            <span class="voice-lang-flag">{{ voiceLangFlag }}</span>
            <span class="voice-lang-text">{{ voiceLangLabel }}</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <div class="voice-lang-menu" *ngIf="langPickerOpen">
            <button type="button" *ngFor="let l of voiceLangs"
                    class="voice-lang-opt" [class.active]="voiceLang === l.code"
                    (click)="selectVoiceLang(l.code)">
              <span class="voice-lang-flag">{{ l.flag }}</span>
              <span>{{ l.label }}</span>
            </button>
          </div>
        </div>
        <div class="voice-popup-actions">
          <button type="button" class="voice-popup-stop" [disabled]="isTranscribing" (click)="closeVoicePopup()">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
            Cancel
          </button>
          <button type="button" class="voice-popup-go"
                  [disabled]="isTranscribing"
                  (click)="confirmVoice()">
            <ng-container *ngIf="isTranscribing">
              <span class="voice-spinner" aria-hidden="true"></span>
              Transcribing…
            </ng-container>
            <ng-container *ngIf="!isTranscribing && !productVoiceActive">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
              Send
            </ng-container>
            <ng-container *ngIf="!isTranscribing && productVoiceActive">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              Search
            </ng-container>
          </button>
        </div>
      </div>

      <!-- Follow-up chips: collapsed to 2 by default; "+N more" pill expands -->
      <div class="chips" *ngIf="suggestions.length > 0 && !isStreaming && !isLoading"
           [attr.data-expanded]="chipsExpanded || null">
        <button class="chip" *ngFor="let c of visibleChips" (click)="selectSuggestion(c)" [title]="c">{{ c }}</button>
        <button class="chip chip-more" *ngIf="hiddenChipsCount > 0 || chipsExpanded"
                (click)="toggleChipsExpanded()"
                [attr.aria-expanded]="chipsExpanded">
          {{ chipsExpanded ? 'Show less' : '+' + hiddenChipsCount + ' more' }}
        </button>
      </div>
      <div class="composer" [attr.data-focused]="composerFocused || null">
        <!-- Staged photo preview — shown above the textarea when an image is attached. -->
        <div class="photo-preview" *ngIf="pendingPhotoDataUrl">
          <img [src]="pendingPhotoDataUrl" alt="Attached photo" />
          <div class="photo-preview-meta">
            <span class="photo-preview-name">{{ pendingPhotoName || 'Photo' }}</span>
            <span class="photo-preview-hint">Tap send to OCR</span>
          </div>
          <button type="button" class="photo-preview-remove"
                  (click)="removePendingPhoto()"
                  title="Remove attached photo"
                  aria-label="Remove attached photo">×</button>
        </div>
        <!-- Staged document chip (PDF/Word/Excel) — extracted + relevance-checked server-side. -->
        <div class="doc-chip" *ngIf="pendingDocName || docBusy">
          <span class="doc-chip-spin" *ngIf="docBusy"></span>
          <span class="doc-chip-icon" *ngIf="!docBusy">📄</span>
          <div class="doc-chip-meta">
            <span class="doc-chip-name">{{ pendingDocName || 'Document' }}</span>
            <span class="doc-chip-hint" *ngIf="docBusy">Reading &amp; checking…</span>
            <span class="doc-chip-hint ready" *ngIf="!docBusy && pendingDocId">Ready{{ docTruncated ? ' · long file, trimmed' : '' }} — tap send</span>
          </div>
          <button type="button" class="photo-preview-remove" *ngIf="!docBusy"
                  (click)="removePendingDoc()" title="Remove document" aria-label="Remove document">×</button>
        </div>
        <!-- Document rejection / error notice. -->
        <div class="doc-error" *ngIf="docError">
          <span>{{ docError }}</span>
          <button type="button" (click)="docError = null" aria-label="Dismiss">×</button>
        </div>
        <textarea
          #composerEl
          [(ngModel)]="inputText"
          (focus)="composerFocused = true"
          (blur)="composerFocused = false"
          (keydown)="onKeyDown($event)"
          (input)="autoResize($event)"
          [disabled]="isStreaming || isLoading"
          placeholder="Ask Shaji anything…"
          rows="1"
          maxlength="2000"></textarea>
        <div class="composer-bar">
          <!-- Attach a document (PDF / Word / Excel) — extracted + relevance-gated server-side. -->
          <input type="file" #docInput
                 accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.ms-excel"
                 style="display:none" (change)="onDocAttached($event)" />
          <button type="button" class="cmp-btn doc-btn"
                  [class.has-doc]="!!pendingDocId"
                  [disabled]="isLoading || isStreaming || docBusy"
                  [title]="pendingDocId ? 'Document attached — tap send' : 'Attach a PDF, Word or Excel document'"
                  (click)="docInput.click()">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="m21 12-8.5 8.5a5 5 0 0 1-7-7L14 5a3.4 3.4 0 0 1 4.8 4.8L10.4 18a1.7 1.7 0 1 1-2.4-2.4l7.4-7.4"/>
            </svg>
          </button>
          <!-- F.2: save the current question as a favorite for one-tap re-run. -->
          <button class="cmp-btn save-btn" type="button"
                  [disabled]="!canSaveFavorite"
                  title="Save this question for quick re-use"
                  (click)="saveFavorite()">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.8 5.9 21.4l1.4-6.8L2.2 9.9l6.9-.8z"/>
            </svg>
            <span>Save</span>
          </button>
          <!-- OCR / attach photo: opens camera on mobile, file picker on desktop. -->
          <input type="file" #photoInput accept="image/*" capture="environment"
                 style="display:none" (change)="onPhotoAttached($event)" />
          <button type="button"
                  class="cmp-btn photo-btn"
                  [class.has-photo]="!!pendingPhotoDataUrl"
                  [disabled]="isLoading || isStreaming"
                  [title]="pendingPhotoDataUrl ? 'Photo attached — tap send to OCR' : 'Take photo / attach invoice for OCR'"
                  (click)="photoInput.click()">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>
            </svg>
          </button>
          <button type="button"
                  class="cmp-btn mic-btn"
                  [class.listening]="isListening && !productVoiceActive"
                  [class.has-voice]="voiceSupported"
                  [disabled]="!voiceSupported || isLoading || isStreaming"
                  [title]="!voiceSupported ? 'Voice input not supported in this browser' : (isListening && !productVoiceActive ? 'Stop listening' : 'Dictate a message')"
                  (click)="toggleListening()">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/>
            </svg>
          </button>
          <!-- Product-voice search button removed from here. The startProductVoice() method
               and productVoiceActive state remain in the TS for reuse elsewhere later. -->
          <div class="cmp-spacer"></div>
          <span class="cmp-hint" *ngIf="composerFocused && !isStreaming && !isLoading">↵ to send · ⇧↵ new line</span>
          <button
            class="send"
            [attr.data-state]="(isLoading || isStreaming) ? 'streaming' : (inputText.trim() ? 'ready' : 'idle')"
            (click)="(isLoading || isStreaming) ? stop() : send()"
            [disabled]="!isLoading && !isStreaming && !inputText.trim()"
            [attr.aria-label]="(isLoading || isStreaming) ? 'Stop' : 'Send'">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" data-icon="arrow">
              <path d="M12 19V5M5 12l7-7 7 7"/>
            </svg>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" data-icon="stop">
              <rect x="7" y="7" width="10" height="10" rx="2"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

  </main>

  <!-- Tap-to-drill options popover -->
  <ng-container *ngIf="drillMenu.visible">
    <div class="ai-drill-backdrop" (click)="closeDrill()"></div>
    <div class="ai-drill-menu" [style.left.px]="drillMenu.x" [style.top.px]="drillMenu.y">
      <div class="ai-drill-menu-head" [title]="drillMenu.value">{{ drillMenu.value }}</div>
      <button type="button" class="ai-drill-opt" *ngFor="let o of drillMenu.options" (click)="runDrill(o)">{{ o.label }}</button>
    </div>
  </ng-container>
</div>
